/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="../models/meevo.dtos.d.ts" />



module Meevo.Components.Common {
    'use strict';

    import SecurityAccessEnum = Meevo.Model.SecurityAccessEnum;

    export interface ISecurityService {
        checkSection(section: string): ng.IPromise<void>;
        checkSecurityKey(key: string): ng.IPromise<SecurityAccessEnum>;
        checkSecurityItem(key: string): ng.IPromise<Model.SecurityItem>;
        checkSecurityItemWithElevation(key: string, elevateIfHidden?: boolean): ng.IPromise<Model.SecurityItem>;

        checkAndCache(keys: Array<string>): ng.IPromise<Array<Meevo.Model.SecurityItem>>;
        hasSecurity(key: string): boolean;
        hasSecurity(key: string, access: SecurityAccessEnum): boolean;
        hasSecurityDenyOrGrant(key: string): boolean;
        isKeyGrant(key: string): boolean;
        docsAndFormsClientTabCheck(key: string): boolean;
        chartsClientTabCheck(key: string): boolean;
        canExecute(key: string, $scope: angular.IScope): ng.IPromise<{}>;
        checkCachedSecurityKey(key: string): number;
        checkCachedSecurityItem(key: string): Model.SecurityItem;
        setRootKeytoHide(key: string): void;
        refreshSecurityKeys(): void;
        refreshSecurityKeys(async: boolean): ng.IPromise<Array<Model.SecurityItem>>;
        emulateLocationSecurity(emulatedSecurity: Array<Model.SecurityItem>);
        showDenyWarning(securityKeyPath: string, securityKey?: string, denyMessage?: string): void;
        checkModuleAccess(securityKey: string): ng.IPromise<SecurityAccessEnum>;
        checkModuleAccessForTenant(moduleId: Meevo.Model.ModuleEnum): ng.IPromise<SecurityAccessEnum>;
        checkModuleEnabledForEffectiveLocation(moduleId: Meevo.Model.ModuleEnum): ng.IPromise<boolean>;
        checkMany(keys: string[]): ng.IPromise<Model.SecurityItem[]> ;
        checkMultipleKeys(...keys: string[]): ng.IPromise<Model.SecurityItem | null>;
    }

    interface ISectionGroup<T> { [section: string]: T }



    // SecurityKeys have this format -
    // Section:Command
    // Section may contain : separated fields for nested sections.
    // i.e. DATA:Client:Main
    // Command is a verb like 'New', 'Add', 'Delete', 'View'
    // * will indicate that all SecurityKeys matching this structure should be returned.
    export class SecurityService implements ISecurityService {

        constructor(
            private $http: angular.IHttpService,
            private $q: angular.IQService,
            private loggingService: Meevo.Components.Logging.ILoggingService,
            private $rootScope: angular.IRootScopeService,
            private appSettings: Meevo.Components.Common.IAppSettings,
            private modalService: Meevo.Components.Modal.IModalService,
            private $translate: angular.translate.ITranslateService,
            private identityService: Meevo.Components.Common.IIdentityService) {
        }

        // Cache entire sections here to know that a section has been requested.
        private static sections: ISectionGroup<Array<Meevo.Model.SecurityItem>> = {};

        // Map from SecurityKey to access here.  For quick lookup.
        private static securityKeys: ISectionGroup<Meevo.Model.SecurityItem> = {};


        private log(msg: string): void {
            this.loggingService.trace("SecurityService: " + msg);
        }

        /**
         * Get all SecurityKeys related to a specified section, or look up the specific ones specified as commands.
         * @param section
         * @param commands
         */
        public checkSection(sectionKeyBase: string): ng.IPromise<void> {

            let sectionKeyWildcard = sectionKeyBase;
            if (sectionKeyBase.charAt(sectionKeyBase.length - 1) !== "%")
                sectionKeyWildcard = sectionKeyBase.concat(':%');

            /*  SecurityKeysReview Are Actually Caching Now.    TODO:Review/Delete SecurityService.sections[section] */

            //  Check if this section has actually been requested before, in which case we'll already have all the relevant SecurityKeys
            if (SecurityService.sections[sectionKeyBase] != null) {
                this.log("Use cached values for section " + sectionKeyBase);
                return this.$q.when(); //use cached values
            }

            // If not cached, send to the server
            return this.check([sectionKeyWildcard]).then((resp: Array<Meevo.Model.SecurityItem>) => {
                SecurityService.sections[sectionKeyBase] = resp;
                resp.forEach((r) => {
                    SecurityService.securityKeys[r.key] = r;
                });
                return;
            });
        }

        /**
         * Function from ISecurityService. Looks at cached values only and returns bool indicating if the SecurityKey is Grant versus anything else
         * this function is synchronize on purpose we needed one for quick checking when we are guaranteed it exists already, so we dont have to do promises
         * everytime
         * @param SecurityKey
         * @param access
         */
        public hasSecurity(key: string, access: SecurityAccessEnum = SecurityAccessEnum.Grant): boolean {

            if (Model.Guard.isNothing(SecurityService.securityKeys[key])) {
                this.log(`SecurityKey=${key} missing from cache`);
                return false;
            } else
                return SecurityService.securityKeys[key].securityAccess == access;

        }
        public hasSecurityDenyOrGrant(key: string): boolean {

            if (Model.Guard.isNothing(SecurityService.securityKeys[key])) {
                this.log(`SecurityKey=${key} missing from cache`);
                return false;
            }
            else if (SecurityService.securityKeys[key].securityAccess == SecurityAccessEnum.Grant) {
                return true;
            } else if (SecurityService.securityKeys[key].securityAccess == SecurityAccessEnum.Deny) {
                return true;
            } else
                return false;
        }

        public docsAndFormsClientTabCheck(key: string): boolean {
            if (Model.Guard.isNothing(SecurityService.securityKeys[key])) {
                this.log(`SecurityKey=${key} missing from cache`);
                return false;
            } else if (SecurityService.securityKeys[key].moduleTenantSecurityAccess !== SecurityAccessEnum.Grant) {
                SecurityService.securityKeys[key].securityAccess = SecurityService.securityKeys[key].roleSecurityAccess;
            }

            return SecurityService.securityKeys[key].securityAccess === SecurityAccessEnum.Grant;
        }
        public chartsClientTabCheck(key: string): boolean {
            if (Model.Guard.isNothing(SecurityService.securityKeys[key])) {
                console.error(`SecurityKey=${key} missing from cache`);
                this.log(`SecurityKey=${key} missing from cache`);
                return false;
            } else if (SecurityService.securityKeys[key].moduleTenantSecurityAccess !== SecurityAccessEnum.Grant) {
                SecurityService.securityKeys[key].securityAccess = SecurityService.securityKeys[key].roleSecurityAccess;
            }

            return SecurityService.securityKeys[key].securityAccess === SecurityAccessEnum.Grant;
        }
        public checkCachedSecurityKey(key: string): number {
            let cachedSecurityItem = SecurityService.securityKeys[key];
            if (_.isUndefined(cachedSecurityItem))
                return 2054;
            return cachedSecurityItem.securityAccess;
        }

        /**
         * Synchronous, cache-only read of a previously-checked security item (or undefined
         * if it hasn't been checked/cached yet). Used where a template needs to read
         * `canElevate` (not just the access level) without awaiting another promise --
         * e.g. the enhanced global menu's Deny+canElevate lock icon (CC.2.2 / EC.3).
         */
        public checkCachedSecurityItem(key: string): Model.SecurityItem {
            return SecurityService.securityKeys[key];
        }

        public setRootKeytoHide(key: string): void {
            SecurityService.securityKeys[key].securityAccess = SecurityAccessEnum.Hide;
        }

        public checkModuleAccess(key: string): ng.IPromise<SecurityAccessEnum> {
            return this.checkSecurityItem(key).then((securityItem : Model.SecurityItem) => {
                return securityItem.moduleSecurityAccess;
            });
        }

        /**
         * Resolves the tenant-level access for a module id by querying
         * api/Module/TenantList. Like checkModuleAccess, but keyed by module id
         * rather than a security key. Resolves Hide when the tenant has no entry.
         */
        public checkModuleAccessForTenant(moduleId: Meevo.Model.ModuleEnum): ng.IPromise<SecurityAccessEnum> {
            return this.$http.post("api/Module/TenantList",
                { moduleId: moduleId, tenantId: this.identityService.current().tenantId })
                .then((resp: angular.IHttpPromiseCallbackArg<any>) => {
                    let data = resp.data;
                    return data && data.length > 0 ? data[0].securityAccess : SecurityAccessEnum.Hide;
                });
        }

        public checkModuleEnabledForEffectiveLocation(moduleId: Meevo.Model.ModuleEnum): ng.IPromise<boolean> {
            return this.$http.get("api/Module/" + moduleId + "/access")
                .then((resp: angular.IHttpPromiseCallbackArg<boolean>) => !!resp.data,
                    () => false);
        }

        /**
         * Returns a promise that resolves with the SecurityKey for a given key
         * Resolves with the cached value if it is available otherwise makes a request to the server.
         * @param SecurityKeyKey
         */
        public checkSecurityKey(key: string): ng.IPromise<SecurityAccessEnum> {
            let deferred = this.$q.defer<SecurityAccessEnum>();
            let cachedSecurityItem = SecurityService.securityKeys[key];
            if (_.isUndefined(cachedSecurityItem)) {
                this.check([key]).then(data => {
                    let securityItem = _.findWhere(data, { key: key });
                    if (!_.isEmpty(securityItem)) {
                        // Add to cache
                        this.addSecurityItemToCache(securityItem);
                        deferred.resolve(securityItem.securityAccess);
                    } else {
                        deferred.reject(null);
                    }
                });
            } else {
                deferred.resolve(cachedSecurityItem.securityAccess);
            }
            return deferred.promise;
        }

        public checkSecurityItem(key: string): ng.IPromise<Model.SecurityItem> {
            let deferred = this.$q.defer<Model.SecurityItem>();
            let cachedSecurityItem = SecurityService.securityKeys[key];
            if (_.isUndefined(cachedSecurityItem)) {
                this.check([key]).then(data => {
                    let securityItem = _.findWhere(data, { key: key });
                    if (!_.isEmpty(securityItem)) {
                        // Add to cache
                        this.addSecurityItemToCache(securityItem);
                        deferred.resolve(securityItem);
                    } else {
                        // Key was never found most likely does not exist in DB
                        let hide: Model.SecurityItem = { key: key, securityAccess: Model.SecurityAccessEnum.Hide}
                        deferred.reject(hide);
                    }
                });
            } else {
                deferred.resolve(cachedSecurityItem);
            }
            return deferred.promise;
        }

        public checkSecurityItemWithElevation(key: string, elevateIfHidden: boolean = false): ng.IPromise<Model.SecurityItem> {
            let deferred = this.$q.defer();
            this.checkSecurityItem(key)
                .then((securityItem: Model.SecurityItem) => {
                    if (securityItem.securityAccess == Meevo.Model.SecurityAccessEnum.Grant) {
                        deferred.resolve(securityItem);
                    } else if (securityItem.securityAccess == Meevo.Model.SecurityAccessEnum.Deny && securityItem.canElevate) {
                        this.$rootScope.$emit(Meevo.Components.Common.Messages.ELEVATION_CHALLENGE, deferred, key.replace(/:/gi, '_'), null, null, securityItem.securityKeyPath);
                    } else {
                        if(elevateIfHidden) {
                            this.$rootScope.$emit(Meevo.Components.Common.Messages.ELEVATION_CHALLENGE, deferred, key.replace(/:/gi, '_'), null, null, securityItem.securityKeyPath);
                        } else {
                            deferred.reject(securityItem);
                        }
                    }
                });

            return deferred.promise;
        }

        public checkMany(keys: string[]): ng.IPromise<Model.SecurityItem[]> {
            return this.check(keys);
        }


        /**
         * Adds an individual SecurityKey to the cache.
         * @param SecurityKey
         */
        private addSecurityItemToCache(securityItem: Model.SecurityItem) {
            if (!_.isEmpty(securityItem)) {
                let existing = SecurityService.securityKeys[securityItem.key];
                if (_.isEmpty(existing)) {
                    SecurityService.securityKeys[securityItem.key] = securityItem;
                }
            }
        }

        //refreshSecurityKeys() {
        //    this.$http.post("/api/Security/Check", { keys: "%" })
        //        .then((resp: angular.IHttpPromiseCallbackArg<Array<Model.SecurityItem>>) => {
        //            resp.data.forEach((securityItem) => {
        //                SecurityService.securityKeys[securityItem.key] = securityItem;
        //            });
        //        });
        //}

        refreshSecurityKeys(): void
        refreshSecurityKeys(async: boolean): angular.IPromise<Array<Model.SecurityItem>>
        refreshSecurityKeys(async: boolean = false): angular.IPromise<Array<Model.SecurityItem>> {
            let keys = ["%"]
            SecurityService.securityKeys = {};
            let promise = this.$http.post("/api/Security/Check", { keys: keys })
                .then((response: angular.IHttpPromiseCallbackArg<Array<Model.SecurityItem>>) => {
                    response.data.forEach((securityItem) => {
                        SecurityService.securityKeys[securityItem.key] = securityItem;
                    });
                    /*  TODO: Review/Delete SecurityService.sections[section] in this.checkSection.  */
                    for (let section in SecurityService.sections)
                        SecurityService.sections[section] = response.data

                    if (async)
                        return response
                });

            if (async)
                return promise

        }

        emulateLocationSecurity(emulatedSecurity: Array<Model.SecurityItem>) {
            if (Model.Guard.isNothing(emulatedSecurity))
                return;

            emulatedSecurity.forEach(securityItem => SecurityService.securityKeys[securityItem.key] = securityItem);
            for (let section in SecurityService.sections)
                SecurityService.sections[section] = emulatedSecurity;
        }

        /**
         * This will not do caching, but it will unwrap the http result of a successful response and return the data.
         * @param commands
         */
        private check(keys: Array<string>): ng.IPromise<Model.SecurityItem[]> {
            this.log("Check SecurityKeys for " + keys.toString());
            var deffered = this.$q.defer<Model.SecurityItem[]>();
            var customKeys = keys.filter(k => k.toLowerCase().indexOf("custom_entity") >= 0);
            if (customKeys.length > 0) {
                let json = { keys: customKeys };
                this.$http.post("/api/Security/Check", json)
                    .then((resp: angular.IHttpPromiseCallbackArg<Model.SecurityItem[]>) => {
                        _.forEach(resp.data,
                            item => {
                                item.key = item.key.toLowerCase();
                                this.addSecurityItemToCache(item);
                            });
                        deffered.resolve(resp.data);
                        return resp.data;
                    }).catch((err) => {
                        console.log(err);
                        return deffered.reject();
                    });
            } else {
                var response = this.appSettings.SECURITY_ACCESS_KEYS.then((keysItem) => {
                    _.forEach(keysItem,
                        item => {
                            this.addSecurityItemToCache(item);
                        });
                    deffered.resolve(keysItem);
                    return keysItem;

                });
            }
            return deffered.promise;
        }
        /**
         * Retrieves the SecurityKey for a collection of keys AND attempts to add them to the cache
         * once they're retrieved. This is an alternative to checkSection where it can be any ad hoc
         * list of keys that don't have to belong to the same section and the request isn't included in the
         * section-level caching mechanism. Internally calls check().
         * @param commands
         */
        public checkAndCache(keys: Array<string>): ng.IPromise<Array<Model.SecurityItem>> {

            //could iterate through and rebuild only ones we need for cache, but from where we use it, its not worth it

            return this.check(keys).then(response => {
                //_.forEach(response, item => {
                //    this.addSecurityItemToCache(item);
                //});
                return response;
            });
        };

        canExecute(key: string, $scope: angular.IScope): ng.IPromise<{}> {
            let defer = this.$q.defer();

            this.checkSecurityItem(key).then((securityItem: Model.SecurityItem) => {
                if (securityItem.securityAccess === Model.SecurityAccessEnum.Grant) {
                    defer.resolve();
                }
                else if (securityItem.canElevate) {
                    $scope.$emit(Meevo.Components.Common.Messages.ELEVATION_CHALLENGE, defer, key.replace(/:/gi, '_'), securityItem.displayName);
                }
            });

            return defer.promise;
        }

        public showDenyWarning(securityKeyPath: string, securityKey?: string, denyMessage: string = 'ERROR_HTTP_403'): void {
            let errorMessage = `${this.$translate.instant(denyMessage)}</br>${securityKeyPath}`;
            this.modalService.alert(errorMessage);
            if (securityKey) {
                this.$http.post('/api/Alert/Trigger/AccessDenied', { securityKey: securityKey });
            }
        }

        /**
         * Method checks multiple security keys and returns first key with securityAccess !== Grant
         * Returns null if all keys have Grant access
         * @param keys
         */
        public checkMultipleKeys(...keys: string[]): ng.IPromise<Model.SecurityItem | null> {
            return this.$q.all(keys.map(key => this.checkSecurityItem(key))).then(result => {
                for (const securityItem of result) {
                    if (securityItem.securityAccess !== Model.SecurityAccessEnum.Grant) {
                        return securityItem;
                    }
                }

                return null;
            });
        }


        public isKeyGrant(key: string): boolean {
            return SecurityService.securityKeys[key].securityAccess === Model.SecurityAccessEnum.Grant
        }

        static factory(): Function {
            let svc = ($http: angular.IHttpService,
                $q: angular.IQService,
                loggingService: Meevo.Components.Logging.ILoggingService,
                $rootScope: angular.IRootScopeService,
                appSettings: Meevo.Components.Common.IAppSettings,
                modalService: Meevo.Components.Modal.IModalService,
                $translate: angular.translate.ITranslateService,
                identityService: Meevo.Components.Common.IIdentityService) => {
                return new SecurityService($http, $q, loggingService, $rootScope, appSettings, modalService, $translate, identityService);
            };
            svc.$inject = [
                '$http',
                '$q',
                'meevo.components.logging.LoggingService',
                '$rootScope',
                'meevo.AppSettings',
                'meevo.components.modal.ModalService',
                '$translate',
                'meevo.components.common.IdentityService'
            ];
            return svc;
        }

    }

    angular.module("meevo.components.common")
        .factory("meevo.components.common.SecurityService", SecurityService.factory());
}
